import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ReferenceArea,
  ReferenceLine,
  Scatter,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";
import "./ReportView.css";

// PAGE: Shows analytics and reports
const ReportsView = ({ orders, dateRange, locationMap, categoryMetrics }) => {
  // HELPER: Group orders by date for line chart
  const groupByDate = () => {
    const grouped = {};

    orders.forEach((order) => {
      const date = new Date(order.pBorrowtime);
      const dateKey = date.toISOString().split("T")[0]; // YYYY-MM-DD
      if (!grouped[dateKey]) grouped[dateKey] = { orders: 0, revenue: 0 };
      grouped[dateKey].orders += 1;
      if (order.pJiesuan > 0) grouped[dateKey].revenue += order.pJiesuan;
    });

    return Object.entries(grouped)
      .map(([date, data]) => ({
        date,
        orders: data.orders,
        revenue: data.revenue,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // HELPER: Prepare data for bar chart (orders per location)
  const getLocationData = () => {
    const locationGroups = {};
    orders.forEach((o) => {
      const loc = o.pShopName || locationMap[o.pCabinetid] || "Unassigned";
      if (!locationGroups[loc]) locationGroups[loc] = { orders: 0, revenue: 0 };
      locationGroups[loc].orders += 1;
      if (o.pJiesuan && o.pJiesuan > 0) {
        locationGroups[loc].revenue += o.pJiesuan;
      }
    });
    return Object.entries(locationGroups).map(([loc, data]) => ({
      location: loc,
      orders: data.orders,
      revenue: data.revenue,
    }));
  };

  // HELPER: Prepare data for pie chart (status distribution)
  const getStatusData = () => {
    const completed = orders.filter(
      (o) =>
        (o.pGhtime && o.pGhtime.trim() !== "") || (o.pJiesuan && o.pJiesuan > 0)
    ).length;
    const pending = orders.length - completed;
    return [
      { name: "Completed", value: completed },
      { name: "Pending", value: pending },
    ];
  };

  // HELPER: Prepare data for histogram (duration distribution)
  // HELPER: Prepare data for histogram (duration distribution) - CLEAN LABELS
  // HELPER: Prepare data for histogram (duration distribution) – CLEAN & READABLE
  const getDurationData = () => {
    const buckets = {};

    orders.forEach((o) => {
      const mins = o.billingDuration || 0;

      let bucketKey;
      let label;

      if (mins < 60) {
        // 0-60 min → 30-min buckets
        const start = Math.floor(mins / 30) * 30;
        bucketKey = start;
        label = `${start}-${start + 29} min`;
      } else {
        // 60+ min → hourly buckets
        const hours = Math.floor(mins / 60);
        bucketKey = hours * 60;
        label =
          hours === 1
            ? "1-2 hrs"
            : hours < 10
            ? `${hours}-${hours + 1} hrs`
            : `${hours}+ hrs`;
      }

      if (!buckets[bucketKey]) buckets[bucketKey] = { label, count: 0 };
      buckets[bucketKey].count += 1;
    });

    // Sort by duration and limit to top 12 buckets
    return Object.values(buckets)
      .sort((a, b) => {
        const aMins = a.label.includes("min")
          ? parseInt(a.label)
          : parseInt(a.label) * 60;
        const bMins = b.label.includes("min")
          ? parseInt(b.label)
          : parseInt(b.label) * 60;
        return aMins - bMins;
      })
      .slice(0, 12)
      .map((b) => ({ bucket: b.label, count: b.count }));
  };

  // HELPER: Prepare data for radar chart (location metrics)
  const getRadarData = () => {
    const locationData = getLocationData();
    return locationData
      .sort((a, b) => b.revenue - a.revenue) // Sort by revenue
      .slice(0, 5)
      .map((loc) => ({
        location:
          loc.location.length > 18
            ? loc.location.slice(0, 16) + "..."
            : loc.location, // Shorten long names
        orders: loc.orders,
        revenue: Math.round(loc.revenue / 100), // Scale revenue properly (was /100 → too small)
        avgDuration: Math.round(
          orders
            .filter(
              (o) => (o.pShopName || locationMap[o.pCabinetid]) === loc.location
            )
            .reduce((sum, o) => sum + (o.billingDuration || 0), 0) /
            loc.orders || 0
        ),
      }));
  };

  // HELPER: Additional summary cards data
  const generateSummaryCards = () => {
    const totalOrders = orders.length;
    const completed = orders.filter(
      (o) =>
        (o.pGhtime && o.pGhtime.trim() !== "") || (o.pJiesuan && o.pJiesuan > 0)
    ).length;
    const pending = totalOrders - completed;
    const totalRevenue = orders.reduce((sum, o) => sum + (o.pJiesuan || 0), 0);
    const avgRevenue =
      completed > 0 ? (totalRevenue / completed).toFixed(2) : 0;
    const avgDuration =
      totalOrders > 0
        ? (
            orders.reduce((sum, o) => sum + (o.billingDuration || 0), 0) /
            totalOrders
          ).toFixed(1)
        : 0;

    // FIND TOP LOCATION BY REVENUE (not by orders)
    const locationData = getLocationData();
    const topLocationByRevenue =
      locationData.length > 0
        ? locationData.reduce((prev, current) =>
            prev.revenue > current.revenue ? prev : current
          )
        : { location: "N/A", revenue: 0, orders: 0 };

    return [
      {
        title: "Total Orders",
        value: totalOrders,
        icon: "fas fa-shopping-cart",
        color: "#3b82f6",
      },
      {
        title: "Completed",
        value: completed,
        icon: "fas fa-check-circle",
        color: "#10b981",
      },
      {
        title: "Pending",
        value: pending,
        icon: "fas fa-clock",
        color: "#f59e0b",
      },
      {
        title: "Total Revenue (Ksh)",
        value: totalRevenue.toFixed(2),
        icon: "fas fa-money-bill-wave",
        color: "#06b6d4",
      },
      {
        title: "Avg Revenue (Ksh)",
        value: avgRevenue,
        icon: "fas fa-chart-line",
        color: "#8b5cf6",
      },
      {
        title: "Avg Duration (min)",
        value: avgDuration,
        icon: "fas fa-stopwatch",
        color: "#ef4444",
      },
      // Unique Users card REMOVED
      {
        title: "Top Location (by Revenue)",
        value: `${
          topLocationByRevenue.location
        } (${topLocationByRevenue.revenue.toFixed(0)} Ksh)`,
        icon: "fas fa-map-marker-alt",
        color: "#14b8a6",
      },
    ];
  };

  // HELPER: Build category comparison data
  const getCategoryComparisonData = () => {
    const categories = [
      { id: "AIRPORT", name: "Airports", color: "#5e52e0" },
      { id: "ENT. FAC", name: "Entertainment", color: "#ef2f6e" },
      { id: "MALLS / HOSPITAL", name: "Malls & Hospitals", color: "#4b6d22" },
    ];

    return categories.map((cat) => {
      const cabinetIds = categoryMetrics[cat.id] || [];
      const ordersInCat = orders.filter((o) =>
        cabinetIds.includes(o.pCabinetid)
      );

      const completed = ordersInCat.filter(
        (o) => (o.pGhtime && o.pGhtime.trim()) || o.pJiesuan > 0
      );

      const totalRevenue = completed.reduce(
        (sum, o) => sum + (o.pJiesuan || 0),
        0
      );
      const avgDurationMins =
        ordersInCat.length > 0
          ? ordersInCat.reduce((sum, o) => sum + (o.billingDuration || 0), 0) /
            ordersInCat.length
          : 0;

      return {
        category: cat.name,
        orders: ordersInCat.length,
        revenue: totalRevenue,
        avgDuration: avgDurationMins / 60, // convert to hours
      };
    });
  };

  // Add this helper inside ReportsView (before return)
  const getCategoryData = () => {
    const cats = [
      { id: "AIRPORT", name: "Airports" },
      { id: "ENT. FAC", name: "Entertainment" },
      { id: "MALLS / HOSPITAL", name: "Malls & Hospitals" },
    ];

    return cats.map((c) => {
      const ids = categoryMetrics[c.id] || [];
      const ords = orders.filter((o) => ids.includes(o.pCabinetid));
      const completed = ords.filter((o) => o.pGhtime?.trim() || o.pJiesuan > 0);
      const revenue = completed.reduce((s, o) => s + (o.pJiesuan || 0), 0);
      const avgMins = ords.length
        ? ords.reduce((s, o) => s + (o.billingDuration || 0), 0) / ords.length
        : 0;

      return {
        category: c.name,
        orders: ords.length,
        revenue,
        avgDuration: Number((avgMins / 60).toFixed(2)), // hours
        completionRate: ords.length
          ? Math.round((completed.length / ords.length) * 100)
          : 0,
      };
    });
  };

  const categoryData = getCategoryData();

  const categoryComparisonData = getCategoryComparisonData();

  const summaryCards = generateSummaryCards();
  const lineData = groupByDate();
  const barData = getLocationData();
  const pieData = getStatusData();
  const histogramData = getDurationData();
  const radarData = getRadarData();

  // Colors for pie chart
  const pieColors = ["#10b981", "#f59e0b", "#3b82f6", "#ef4444"];

  // Render the page
  return (
    <div className="reports-view">
      <h2 className="section-header">
        <i className="fas fa-chart-bar"></i> Reports & Analytics
        <span className="chart-date-subtitle">
          Period:{" "}
          {dateRange?.start?.toLocaleDateString("en-KE", {
            month: "short",
            day: "numeric",
          })}{" "}
          –{" "}
          {dateRange?.end?.toLocaleDateString("en-KE", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>{" "}
      </h2>

      {/* Summary Cards */}
      <div className="summary-cards-grid">
        {summaryCards.map((card, idx) => (
          <div
            key={idx}
            className="summary-card"
            style={{ borderLeftColor: card.color }}
          >
            <div className="summary-icon">
              <i className={card.icon}></i>
            </div>
            <div className="summary-content">
              <div className="summary-label">{card.title}</div>
              <div className="summary-value">{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 1. Revenue Lollipop Chart */}
      <div className="chart-section">
        <h3 className="chart-title">
          Revenue Lollipop Chart{/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={categoryData} layout="vertical">
            <XAxis type="number" />
            <YAxis dataKey="category" type="category" width={160} />
            <Tooltip formatter={(v) => `Ksh ${v.toLocaleString()}`} />

            {/* Thin line */}
            <Bar dataKey="revenue" fill="transparent" barSize={4} />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#94a3b8"
              strokeWidth={3}
              dot={false}
            />

            {/* Big dot at the end */}
            <Scatter
              dataKey="revenue"
              fill={(d) =>
                d.category === "Airports"
                  ? "#5e52e0"
                  : d.category === "Entertainment"
                  ? "#ef2f6e"
                  : "#4b6d22"
              }
              shape="circle"
              r={12}
            />

            <text
              x="50%"
              y="30"
              textAnchor="middle"
              fontSize="18"
              fill="#475569"
            >
              Revenue Comparison
            </text>
          </BarChart>
        </ResponsiveContainer>
        <p className="chart-note">
          Quick visual ranking of total revenue across Airports, Entertainment
          venues, and Malls/Hospitals.
        </p>
      </div>

      {/* 2. Normalized Area Chart */}
      <div className="chart-section">
        <h3 className="chart-title">
          Category Metrics (Normalized Area){/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={categoryData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="orders"
              stackId="1"
              stroke="#3b82f6"
              fill="#6366f1"
              fillOpacity={0.7}
            />
            <Area
              type="monotone"
              dataKey={(d) => d.revenue / 1000}
              stackId="1"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.7}
              name="Revenue (x1000)"
            />
            <Area
              type="monotone"
              dataKey="avgDuration"
              stackId="1"
              stroke="#f59e0b"
              fill="#f59e0b"
              fillOpacity={0.7}
              name="Avg Duration (hrs)"
            />
          </AreaChart>
        </ResponsiveContainer>
        <p className="chart-note">
          Stacked comparison of Orders, Revenue (in thousands), and Average
          Duration (hours) across categories.
        </p>
      </div>

      {/* 3. Mini-Charts */}
      <div className="chart-section">
        <h3 className="chart-title">
          Category Mini-Charts{/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {["orders", "revenue"].map((metric) => (
            <div
              key={metric}
              className="chart-section"
              style={{ padding: "1rem", paddingBottom: "2.5rem" }} // Increased bottom padding
            >
              <h4 style={{ textAlign: "center", marginBottom: "1rem" }}>
                {metric === "orders" ? "Orders" : "Revenue"}
              </h4>
              <ResponsiveContainer width="100%" height={400}>
                {" "}
                {/* Increased height from 220 → 260 */}
                <BarChart
                  data={categoryData.sort((a, b) => b[metric] - a[metric])}
                  margin={{ top: 10, right: 10, left: 10, bottom: 10 }} // Critical: extra bottom margin for rotated labels
                >
                  <XAxis
                    dataKey="category"
                    angle={-45}
                    textAnchor="end"
                    height={90} // Gives more room for the angled text
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value) =>
                      metric === "revenue"
                        ? `Ksh ${Number(value).toLocaleString()}`
                        : value
                    }
                  />
                  <Bar
                    dataKey={metric}
                    fill={metric === "revenue" ? "#10b981" : "#6366f1"}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
        <p className="chart-note">
          Side-by-side breakdown: Total Orders and Revenue per category (sorted
          high to low).
        </p>
      </div>

      {/* 4. Performance by Location Category */}
      <div className="chart-section">
        <h3 className="chart-title">
          Performance by Location Category{/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <div className="chart-scroll-wrapper">
          <div className="chart-fixed-container" style={{ minWidth: "800px" }}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={categoryComparisonData}
                margin={{ top: 30, right: 40, left: 60, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" />

                <XAxis
                  dataKey="category"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 13, fill: "#475569" }}
                />

                {/* Left Y-Axis: Orders & Revenue */}
                <YAxis
                  yAxisId="left"
                  label={{
                    value: "Orders & Revenue (Ksh)",
                    angle: -90,
                    position: "insideLeft",
                    style: { fill: "#64748b" },
                  }}
                />

                {/* Right Y-Axis: Duration (hours) */}
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: "Avg Duration (hours)",
                    angle: 90,
                    position: "insideRight",
                    style: { fill: "#f59e0b" },
                  }}
                  domain={[0, "dataMax + 1"]}
                  tickFormatter={(v) => v.toFixed(1)} // shorten decimals on axis
                />

                <Tooltip
                  formatter={(value, name) => [
                    name === "Avg Duration"
                      ? `${value.toFixed(2)} hrs`
                      : name === "Revenue"
                      ? `Ksh ${Number(value).toLocaleString()}`
                      : value,
                    name,
                  ]}
                  contentStyle={{
                    background: "#1e293b",
                    border: "none",
                    borderRadius: "8px",
                  }}
                />

                <Legend verticalAlign="top" height={40} iconType="rect" />

                {/* Orders Bar */}
                <Bar
                  yAxisId="left"
                  dataKey="orders"
                  name="Total Orders"
                  fill="#3b82f6"
                  radius={[8, 8, 0, 0]}
                  barSize={40}
                  label={false} // prevent top numeric labels
                />

                {/* Revenue Bar */}
                <Bar
                  yAxisId="left"
                  dataKey="revenue"
                  name="Revenue"
                  fill="#10b981"
                  radius={[8, 8, 0, 0]}
                  barSize={40}
                  label={false} // prevent top numeric labels
                />

                {/* Avg Duration Line (on right axis) */}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="avgDuration"
                  name="Avg Duration"
                  stroke="#f59e0b"
                  strokeWidth={4}
                  dot={{ fill: "#f59e0b", r: 8 }}
                  activeDot={{ r: 10 }}
                  label={false} // prevent value labels on the line dots
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <p className="chart-note">
          Bars show Total Orders & Revenue • Orange line shows Average Rental
          Duration in hours across the three location types.
        </p>
      </div>

      {/* 5. Orders & Revenue Over Time */}
      <div className="chart-section">
        <h3 className="chart-title">
          Orders & Revenue Trend Over Time{/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <div style={{ width: "100%", minHeight: "400px" }}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={lineData}
              margin={{ top: 20, right: 50, left: 20, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                angle={-35}
                textAnchor="end"
                height={80}
                interval={lineData.length > 20 ? 4 : "preserveStartEnd"} // Better spacing when many dates
              />
              <YAxis
                yAxisId="left"
                stroke="#3b82f6"
                label={{ value: "Orders", angle: -90, position: "insideLeft" }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#10b981"
                label={{
                  value: "Revenue (Ksh)",
                  angle: 90,
                  position: "insideRight",
                }}
              />
              <Tooltip formatter={(value) => value.toLocaleString()} />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="orders"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ r: 4 }}
                name="Orders"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ r: 4 }}
                name="Revenue (Ksh)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="chart-note">
          Daily order volume (blue) and daily revenue (green) during the
          selected period.
        </p>
      </div>

      {/* 6. Orders & Revenue per Location */}
      <div className="chart-section">
        <h3 className="chart-title">
          Orders & Revenue by Individual Location{/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <ResponsiveContainer width="100%" height={800}>
          {" "}
          {/* Increased height */}
          <BarChart
            data={barData}
            margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="location"
              angle={-60}
              textAnchor="end"
              height={140}
              tick={{ fontSize: 12 }}
            />
            <YAxis />
            <Tooltip
              formatter={(value) =>
                typeof value === "number" ? value.toLocaleString() : value
              }
            />
            <Legend verticalAlign="top" height={40} />
            <Bar dataKey="orders" fill="#3b82f6" name="Orders" />
            <Bar dataKey="revenue" fill="#10b981" name="Revenue (Ksh)" />
          </BarChart>
        </ResponsiveContainer>
        <p className="chart-note">
          Detailed performance breakdown for every cabinet location — scroll
          horizontally if needed.
        </p>
      </div>

      {/* 7. Rental Duration Distribution */}
      <div className="chart-section">
        <h3 className="chart-title">
          Rental Duration Distribution{/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={histogramData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" />
            <XAxis
              dataKey="bucket"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 12, fill: "#475569" }}
            />
            <YAxis
              label={{
                value: "Number of Rentals",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip
              formatter={(value) => [`${value} rentals`, "Count"]}
              contentStyle={{
                backgroundColor: "#1e293b",
                border: "none",
                borderRadius: "8px",
              }}
            />
            <Bar dataKey="count" fill="#f59e0b" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="chart-note">
          How long customers typically rent power banks — automatically switches
          from minutes to hours for longer rentals.
        </p>
      </div>

      {/* 8. Top 5 Locations Spider Chart */}
      <div className="chart-section">
        <h3 className="chart-title">
          Top 5 Locations Performance Comparison{/* ADD THIS SUBTITLE */}
          <span className="chart-date-subtitle">
            Period:{" "}
            {dateRange?.start?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
            })}{" "}
            –{" "}
            {dateRange?.end?.toLocaleDateString("en-KE", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </h3>
        <ResponsiveContainer width="100%" height={520}>
          <RadarChart data={radarData} outerRadius="82%">
            {/* Light grid – no numbers */}
            <PolarGrid stroke="#e2e8f0" strokeWidth={1} />

            {/* Location names only – clean & bold */}
            <PolarAngleAxis
              dataKey="location"
              tick={{ fontSize: 13, fontWeight: 700, fill: "#1e293b" }}
              tickLine={false}
            />

            {/* COMPLETELY HIDE ALL RADIUS NUMBERS */}
            <PolarRadiusAxis
              tick={false}
              axisLine={false}
              tickLine={false}
              domain={[0, "auto"]}
            />

            {/* Orders */}
            <Radar
              name="Orders"
              dataKey="orders"
              stroke="#6366f1"
              fill="#6366f1"
              fillOpacity={0.65}
              strokeWidth={4}
              dot={{ r: 6, fill: "#6366f1", strokeWidth: 2 }}
            />

            {/* Revenue */}
            <Radar
              name="Revenue"
              dataKey="revenue"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.65}
              strokeWidth={4}
              dot={{ r: 6, fill: "#10b981", strokeWidth: 2 }}
            />

            {/* Avg Duration */}
            <Radar
              name="Avg Duration (min)"
              dataKey="avgDuration"
              stroke="#f59e0b"
              fill="#f59e0b"
              fillOpacity={0.65}
              strokeWidth={4}
              dot={{ r: 6, fill: "#f59e0b", strokeWidth: 2 }}
            />

            {/* Rich Tooltip – shows EVERYTHING on hover */}
            <Tooltip
              contentStyle={{
                backgroundColor: "#1e293b",
                border: "none",
                borderRadius: "12px",
                padding: "12px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              }}
              formatter={(value, name, props) => {
                if (name === "Revenue") {
                  return `Ksh ${(value * 100).toLocaleString()}`;
                }
                if (name === "Avg Duration (min)") {
                  return `${value} minutes`;
                }
                return value;
              }}
              labelStyle={{
                color: "#e2e8f0",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
              itemStyle={{ padding: "6px 0", fontSize: "14px" }}
            />

            <Legend
              verticalAlign="bottom"
              height={50}
              iconType="circle"
              wrapperStyle={{ paddingTop: "30px" }}
            />
          </RadarChart>
        </ResponsiveContainer>
        <p className="chart-note">
          Multi-dimensional view of the best-performing locations. Hover over
          dots to see exact Orders, Revenue, and Avg Duration.
        </p>
      </div>

      {/* Activity Summary */}
      {/* DYNAMIC Activity Summary – Real Insights Based on Data */}
      <div className="activity-summary">
        <h3>Key Insights & Trends</h3>
        <div className="trend-info">
          {/* 1. Overall Trend */}
          {lineData.length >= 7 ? (
            (() => {
              const recent = lineData.slice(-7);
              const older = lineData.slice(-14, -7);
              const recentAvg =
                recent.reduce((s, d) => s + d.orders, 0) / recent.length;
              const olderAvg = older.length
                ? older.reduce((s, d) => s + d.orders, 0) / older.length
                : recentAvg;
              const growth = ((recentAvg - olderAvg) / olderAvg) * 100;
              return (
                <p>
                  {growth > 15 ? (
                    <>
                      <i
                        className="fas fa-arrow-trend-up"
                        style={{ color: "#10b981" }}
                      ></i>{" "}
                      <strong>Strong growth:</strong> Daily orders up{" "}
                      {Math.round(growth)}% vs previous week
                    </>
                  ) : growth > 5 ? (
                    <>
                      <i
                        className="fas fa-arrow-trend-up"
                        style={{ color: "#10b981" }}
                      ></i>{" "}
                      Orders trending upward ({Math.round(growth)}% increase)
                    </>
                  ) : growth < -10 ? (
                    <>
                      <i
                        className="fas fa-arrow-trend-down"
                        style={{ color: "#ef4444" }}
                      ></i>{" "}
                      Orders declining ({Math.round(Math.abs(growth))}% drop)
                    </>
                  ) : (
                    <>
                      <i className="fas fa-minus"></i> Order volume stable this
                      week
                    </>
                  )}
                </p>
              );
            })()
          ) : (
            <p>
              <i className="fas fa-info-circle"></i> Collecting trend data...
            </p>
          )}

          {/* 2. Top Performer */}
          {(() => {
            const topLoc = barData.reduce(
              (a, b) => (a.revenue > b.revenue ? a : b),
              { location: "N/A" }
            );
            return (
              <p>
                <i className="fas fa-trophy" style={{ color: "#f59e0b" }}></i>
                <strong>Top performer:</strong> {topLoc.location} leading in
                revenue
              </p>
            );
          })()}

          {/* 3. Category Leader */}
          {(() => {
            const topCat = categoryData.reduce((a, b) =>
              a.revenue > b.revenue ? a : b
            );
            return (
              <p>
                <i className="fas fa-star" style={{ color: "#8b5cf6" }}></i>
                {topCat.category} generating the highest revenue among
                categories
              </p>
            );
          })()}

          {/* 4. Duration Insight */}
          {(() => {
            const mostCommon = histogramData.reduce(
              (a, b) => (a.count > b.count ? a : b),
              { bucket: "N/A" }
            );
            return (
              <p>
                <i className="fas fa-clock" style={{ color: "#06b6d4" }}></i>
                Most rentals last <strong>{mostCommon.bucket}</strong>
              </p>
            );
          })()}

          {/* 5. Completion Rate */}
          {(() => {
            const rate =
              Math.round(
                (summaryCards[1].value / summaryCards[0].value) * 100
              ) || 0;
            return (
              <p>
                <i
                  className="fas fa-check-circle"
                  style={{
                    color:
                      rate >= 90
                        ? "#10b981"
                        : rate >= 70
                        ? "#f59e0b"
                        : "#ef4444",
                  }}
                ></i>
                Completion rate: <strong>{rate}%</strong>{" "}
                {rate >= 90
                  ? "(Excellent!)"
                  : rate >= 70
                  ? "(Good)"
                  : "(Needs attention)"}
              </p>
            );
          })()}

          {/* 6. Active Locations */}
          <p>
            <i
              className="fas fa-map-marker-alt"
              style={{ color: "#14b8a6" }}
            ></i>
            <strong>{barData.length}</strong> locations currently active
          </p>

          {/* 7. Peak Day (if enough data) */}
          {lineData.length >= 3 &&
            (() => {
              const peakDay = lineData.reduce((a, b) =>
                a.revenue > b.revenue ? a : b
              );
              const formattedDate = new Date(peakDay.date).toLocaleDateString(
                "en-US",
                {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                }
              );
              return (
                <p>
                  <i className="fas fa-fire" style={{ color: "#ef4444" }}></i>
                  Peak revenue day: {formattedDate} (Ksh{" "}
                  {peakDay.revenue.toLocaleString()})
                </p>
              );
            })()}
        </div>
      </div>
    </div>
  );
};

export default ReportsView;
